import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'
import { api, ErroreApi, type VoceCatalogo } from '../servizi/api.ts'
import { useAree, useCatalogo, useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione } from '../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, EtichettaArea, Modale, Riquadro, Scelta, Spunta } from '../componenti/base.tsx'
import { CampoModulo, etichettaCampo, usaModulo } from '../componenti/modulo.tsx'
import { DomandaUscita, usaUscitaProtetta } from '../componenti/uscita.tsx'
import { useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import { nomeSede, opzioniSedi } from '../servizi/sedi.ts'
import {
  MINUTI_AMMESSI, ORE_MASSIME, campiDaMinuti, etichettaProgetto, minutiDaCampi, progettiDaProporre,
  type ProgettoSelezionabile,
} from '../servizi/regole-intervento.ts'
import {
  AvvisoFuoriOrario, CampiRicontatto, ETICHETTE_RICONTATTO, controllaRicontatto, corpoRicontatto, ricontattoVuoto,
  useRicontattiScheda, type ValoriRicontatto,
} from '../componenti/ricontatti.tsx'

type Percorso = {
  id: string; tipologia_codice: string; tipologia: string; area_codice: string
  stato: string; data_apertura: string; sede_id: string | null; progetto_id: string | null
}
type Scheda = {
  id: string; etichetta: string; numero_scheda: number; percorsi: Percorso[]
  data_nascita: string | null; precisione_nascita: string
  categorie: { categoria: string; nome: string; confermata: boolean }[]
  /** Sede della scheda: e' quella proposta per il nuovo percorso (19/10/2026). */
  sede_riferimento: string | null
  puo?: { registrareConsensi?: boolean }
}
type Dettaglio = { campo: string; messaggio: string }

/** GET /interventi/verifica-consensi (D-08). */
type VerificaConsensi = { esito: 'ammesso' | 'avviso' | 'bloccato'; scheda_migrata: boolean; dettagli: Dettaglio[] }

/** GET /interventi/:id, per la modifica e il completamento. */
type Registrato = {
  id: string; persona_id: string; percorso_id: string; data: string; durata_minuti: number | null
  modalita: string; ore_mediazione: number | null
  tipologia_codice: string; tipologia: string; area_codice: string
  sottotipologia_codice: string | null
  stato: string; esito: string | null; dati: Record<string, unknown> | null
  sede_id: string | null; sede: string | null; progetto_id: string | null; attivita_istituzionale: boolean
  stakeholder_id: string | null; stakeholder: string | null; ha_stakeholder: boolean | null
  comune_intervento: string | null; comune: string | null; comune_provincia: string | null
  categoria_persona: string | null; categoria_nascosta: boolean
  fascia_eta: string | null; fascia_eta_manuale: boolean
  note: string | null; note_visibili: boolean; modificabile: boolean
}

/**
 * Stato del modulo a video. Un solo oggetto: il confronto con l'apertura dice se ci sono modifiche. La durata
 * sta in due campi, ore e minuti, entrambi facoltativi (4/11/2026); il campo «Tema» non c'e' piu': gli
 * interventi nuovi non lo hanno, e la modifica non tocca quello gia' registrato.
 */
type Valori = {
  percorso_id: string; area: string; tipologia: string
  sottotipologia_codice: string
  data: string; durata_ore: string; durata_min: string; ore_mediazione: string; modalita: string; sede_id: string
  comune_intervento: string; comune_testo: string
  categoria_persona: string; fascia_eta: string
  ha_stakeholder: '' | 'si' | 'no'; stakeholder_id: string; stakeholder_testo: string
  attivita_istituzionale: boolean; progetto_id: string
  esito: string; note: string
  dati: Record<string, unknown>
}

const oggi = () => new Date().toISOString().slice(0, 10)

const VUOTI: Valori = {
  percorso_id: '', area: '', tipologia: '', sottotipologia_codice: '',
  data: '', durata_ore: '', durata_min: '', ore_mediazione: '', modalita: '', sede_id: '',
  comune_intervento: '', comune_testo: '', categoria_persona: '', fascia_eta: '',
  ha_stakeholder: '', stakeholder_id: '', stakeholder_testo: '',
  attivita_istituzionale: false, progetto_id: '', esito: '', note: '', dati: {},
}

/** Campi fissi dell'intervento: i messaggi dell'API li nominano gia'. */
const CAMPI_FISSI = new Set([
  'data', 'durata_minuti', 'ore_mediazione', 'modalita', 'comune_intervento', 'categoria_persona',
  'fascia_eta', 'ha_stakeholder', 'stakeholder_id', 'progetto_id', 'tipologia_codice', 'sede_id',
  'TRATTAMENTO_DATI', 'CATEGORIE_PARTICOLARI',
])

/** Eta' in anni compiuti alla data indicata; null se la data precede la nascita. */
function etaAl(nascita: string, al: string): number | null {
  if (al < nascita) return null
  const [an, mn, gn] = nascita.split('-').map(Number)
  const [a, m, g] = al.split('-').map(Number)
  return a - an - (m < mn || (m === mn && g < gn) ? 1 : 0)
}

/**
 * Registrazione, completamento e modifica di un intervento.
 *
 * Si sceglie il percorso, oppure area e tipologia di un percorso nuovo, poi si compila
 * l'intervento. Il percorso nuovo si apre insieme all'intervento, nella stessa chiamata: se
 * l'intervento non si registra non resta un percorso vuoto.
 *
 * «Salva» porta l'intervento allo stato completo e chiede i campi di chiusura delle Note
 * aggiuntive: comune, tipologia dell'area, categoria di utenti, fascia d'eta', ente coinvolto (e
 * quale), progetto o attivita' istituzionale. La durata, in ore e minuti, e' facoltativa (551). «Salva
 * come bozza» conserva quanto inserito. «Errore – non salvare
 * scheda» esce senza salvare e lascia nel registro eventi solo l'annotazione dello scarto.
 * Uscendo con modifiche pendenti la pagina chiede «Salva» o «Non salvare».
 *
 * Sulle schede nuove senza i consensi 1 e 2 i comandi di salvataggio restano disattivati (D-08);
 * sulle schede migrate compare l'avviso e si registra.
 *
 * `?percorso=nuovo` apre il modulo con «nuovo percorso» gia' scelto («Apri un percorso» nella
 * linguetta Percorsi), `?percorso=<id>` su quel percorso. La sede del nuovo percorso parte da quella
 * della scheda; su un percorso esistente la sede e' la sua (segnalazioni del 19/10/2026).
 */
export function RegistraIntervento() {
  const { id = '', interventoId } = useParams()
  const [parametri] = useSearchParams()
  const percorsoRichiesto = parametri.get('percorso')
  const modifica = !!interventoId
  const { tassonomia, sedi, io } = useSessione()
  const aree = useAree()
  const modalita = useCatalogo('modalita_erogazione')
  const categorie = useCatalogo('categoria_persona')
  const fasce = useCatalogo('fascia_eta')

  const [scheda, setScheda] = useState<Scheda | null>(null)
  const [registrato, setRegistrato] = useState<Registrato | null>(null)
  const [verifica, setVerifica] = useState<VerificaConsensi | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [codiceErrore, setCodiceErrore] = useState<string | null>(null)
  const [dettagli, setDettagli] = useState<Dettaglio[]>([])
  const [inCorso, setInCorso] = useState(false)
  const [confermaScarto, setConfermaScarto] = useState(false)
  // Ogni salvataggio non riuscito porta il fuoco sul primo campo segnalato: il modulo e' lungo e i
  // comandi stanno in fondo.
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLDivElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  const [valori, setValori] = useState<Valori>(VUOTI)
  // «Fissa anche un ricontatto» (465): solo registrando un intervento nuovo, a chi gestisce i
  // ricontatti della scheda. Finisce sul percorso dell'intervento, nella stessa registrazione.
  const ricontatti = useRicontattiScheda(id, 0)
  const [conRicontatto, setConRicontatto] = useState(false)
  const [ricontatto, setRicontatto] = useState<ValoriRicontatto>(() => ricontattoVuoto(io?.id))
  // Il ricontatto cade con la sede chiusa (556): l'avviso e come ripetere il salvataggio confermando.
  const [fuoriOrario, setFuoriOrario] = useState<{ testo: string; stato: 'bozza' | 'completo' | null
    verso: boolean } | null>(null)
  const [iniziali, setIniziali] = useState<Valori | null>(null)
  const cambia = <K extends keyof Valori>(chiave: K, valore: Valori[K]) =>
    setValori((v) => ({ ...v, [chiave]: valore }))

  const [comuni, setComuni] = useState<{ codice_istat: string; nome: string; provincia_sigla: string }[]>([])
  const [enti, setEnti] = useState<{ id: string; nome: string; sigla: string | null }[]>([])
  const [entiConsultabili, setEntiConsultabili] = useState(true)
  const [progetti, setProgetti] = useState<ProgettoSelezionabile[]>([])
  // «Mostra tutti i progetti»: per difetto la tendina ha i soli progetti della sede (4/11/2026).
  const [tuttiProgetti, setTuttiProgetti] = useState(false)

  useEffect(() => {
    let vivo = true
    async function carica() {
      try {
        const s = await api.get<Scheda>(`/persone/${id}`)
        const [v, r] = await Promise.all([
          // Senza esito dei consensi la pagina resta usabile: decide comunque l'API.
          api.get<VerificaConsensi>(`/interventi/verifica-consensi?persona_id=${id}`).catch(() => null),
          interventoId ? api.get<Registrato>(`/interventi/${interventoId}`) : Promise.resolve(null),
        ])
        if (!vivo) return
        let apertura: Valori
        if (r) {
          // I minuti registrati restano quelli: 78 sono 1 ora e 18 minuti, senza arrotondare ai quarti d'ora.
          const durata = campiDaMinuti(r.durata_minuti)
          apertura = {
            ...VUOTI,
            percorso_id: r.percorso_id, area: r.area_codice, tipologia: r.tipologia_codice,
            sottotipologia_codice: r.sottotipologia_codice ?? '',
            data: r.data.slice(0, 10), durata_ore: durata.ore, durata_min: durata.minuti,
            ore_mediazione: r.ore_mediazione == null ? '' : String(r.ore_mediazione),
            modalita: r.modalita,
            comune_intervento: r.comune_intervento ?? '',
            comune_testo: r.comune ? `${r.comune} (${r.comune_provincia})` : '',
            categoria_persona: r.categoria_persona ?? '', fascia_eta: r.fascia_eta ?? '',
            ha_stakeholder: r.ha_stakeholder == null ? '' : r.ha_stakeholder ? 'si' : 'no',
            stakeholder_id: r.stakeholder_id ?? '', stakeholder_testo: r.stakeholder ?? '',
            attivita_istituzionale: r.attivita_istituzionale, progetto_id: r.progetto_id ?? '',
            esito: r.esito ?? '', note: r.note ?? '', dati: r.dati ?? {},
          }
        } else {
          // Proposta della categoria, se la scheda ne ha una sola confermata. Il percorso si propone
          // piu' sotto, quando la tassonomia dice quali tipologie sono in uso.
          const confermate = s.categorie.filter((c) => c.confermata)
          apertura = {
            ...VUOTI, data: oggi(), sede_id: s.sede_riferimento ?? '',
            categoria_persona: confermate.length === 1 ? confermate[0].categoria : '',
          }
        }
        setScheda(s); setVerifica(v); setRegistrato(r); setValori(apertura); setIniziali(apertura)
      } catch (e) {
        if (vivo) setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      }
    }
    void carica()
    // Tendina del progetto: i progetti a cui l'intervento si puo' legare, anche fuori dall'elenco dei
    // progetti visibili a un incarico di sede, con le sedi collegate.
    api.get<ProgettoSelezionabile[]>('/progetti/selezionabili?limite=500')
      .then((p) => { if (vivo) setProgetti(p) }).catch(() => {})
    return () => { vivo = false }
  }, [id, interventoId])

  const percorso = scheda?.percorsi.find((p) => p.id === valori.percorso_id) ?? null
  const tipologiaScelta = percorso?.tipologia_codice ?? valori.tipologia
  const schemaModulo = usaModulo(tipologiaScelta)

  // Le ore di mediazione (D-27) le chiede la tipologia con il suo segnale, non un codice scritto
  // qui: se CIDIS estende la regola ad altre tipologie, il campo compare senza rilascio.
  const conOre = !!tassonomia?.tipologie.find((t) => t.codice === tipologiaScelta)?.con_ore_mediazione

  // Fascia d'eta' (D-21): con la data di nascita si calcola alla data dell'intervento e si mostra
  // soltanto; senza, si sceglie. Il valore vincolante lo calcola l'API.
  const conDataNascita = !!scheda?.data_nascita && scheda.precisione_nascita !== 'ignota'
  const fasciaCalcolata = useMemo(() => {
    if (!conDataNascita || !scheda?.data_nascita || !valori.data) return null
    const eta = etaAl(scheda.data_nascita.slice(0, 10), valori.data)
    if (eta === null) return null
    const voci = fasce.voci as (VoceCatalogo & { eta_min: number; eta_max: number | null })[]
    return voci.find((f) => eta >= f.eta_min && eta <= (f.eta_max ?? 200))?.codice ?? null
  }, [conDataNascita, scheda, valori.data, fasce.voci])

  useEffect(() => {
    const t = setTimeout(() => {
      if (valori.comune_testo.trim().length < 2) return
      api.get<typeof comuni>(`/riferimenti/comuni?cerca=${encodeURIComponent(valori.comune_testo.trim())}&limite=20`)
        .then(setComuni).catch(() => {})
    }, 300)
    return () => clearTimeout(t)
  }, [valori.comune_testo])

  useEffect(() => {
    if (valori.ha_stakeholder !== 'si' || valori.stakeholder_testo.trim().length < 2) return
    const t = setTimeout(() => {
      // Gli enti selezionabili, non l'anagrafica degli enti: GET /enti chiede ente.leggi, che EXT non ha
      // pur registrando interventi, e con «Ente coinvolto» Sì l'ente e' obbligatorio (D-22).
      api.get<typeof enti>(`/persone/enti-selezionabili?cerca=${encodeURIComponent(valori.stakeholder_testo.trim())}&limite=20`)
        .then((righe) => { setEnti(righe); setEntiConsultabili(true) })
        .catch((e) => { if (e instanceof ErroreApi && e.stato === 404) setEntiConsultabili(false) })
    }, 300)
    return () => clearTimeout(t)
  }, [valori.ha_stakeholder, valori.stakeholder_testo])

  const tipologieAperte = useMemo(
    () => (tassonomia?.tipologie ?? []).filter((t) => t.attiva && !t.storica),
    [tassonomia])
  const areeConTipologie = (tassonomia?.aree ?? [])
    .filter((a) => a.attiva && tipologieAperte.some((t) => t.area_codice === a.codice))

  // Percorsi su cui registrare: non chiusi e su una tipologia in uso. GET /persone/:id restituisce
  // anche i percorsi migrati rimasti aperti su tipologie storiche o non attive: su quelli l'API
  // accetta solo la bozza e l'operatore lo scoprirebbe dopo «Salva» (D-04).
  const percorsiUsabili = useMemo(
    () => (scheda?.percorsi ?? []).filter((p) => p.stato !== 'chiuso'
      && tipologieAperte.some((t) => t.codice === p.tipologia_codice)),
    [scheda, tipologieAperte])

  // Proposta del percorso: quello chiesto dall'indirizzo, altrimenti il primo aperto fra quelli
  // usabili; con `?percorso=nuovo` nessuno. Si calcola una volta per scheda, quando la tassonomia e'
  // disponibile, e solo sul modulo ancora com'era all'apertura, per non sostituire una scelta gia'
  // fatta. Senza tassonomia la pagina resta usabile, senza proposta.
  const propostaPer = useRef<string | null>(null)
  useEffect(() => {
    if (modifica || !scheda || !iniziali || !tassonomia || propostaPer.current === scheda.id) return
    propostaPer.current = scheda.id
    if (JSON.stringify(valori) !== JSON.stringify(iniziali) || percorsoRichiesto === 'nuovo') return
    const proposto = percorsiUsabili.find((p) => p.id === percorsoRichiesto)?.id
      ?? percorsiUsabili.find((p) => p.stato === 'aperto')?.id
    if (!proposto) return
    const apertura = { ...iniziali, percorso_id: proposto }
    setValori(apertura); setIniziali(apertura)
  }, [modifica, scheda, iniziali, tassonomia, percorsiUsabili, valori, percorsoRichiesto])

  const modificato = iniziali !== null && JSON.stringify(valori) !== JSON.stringify(iniziali)
  const corpoScarto = { persona_id: id, intervento_id: interventoId ?? null }
  const uscita = usaUscitaProtetta(modificato, () => {
    // Chiusura della scheda del browser confermata: l'invio sopravvive alla pagina.
    navigator.sendBeacon?.('/api/interventi/scarto',
      new Blob([JSON.stringify(corpoScarto)], { type: 'application/json' }))
  })

  const bloccato = verifica?.esito === 'bloccato'
  const statoRegistrato = registrato?.stato ?? null
  /** «Salva come bozza» c'e' sulla registrazione e sulle bozze. */
  const bozzaPossibile = !modifica || statoRegistrato === 'bozza'

  /** Campi dell'intervento come li vuole l'API, calcolati dai valori a video. */
  function campiApi(v: Valori): Record<string, unknown> {
    return {
      data: v.data,
      // Ore e minuti in minuti; null senza nessuno dei due (551).
      durata_minuti: minutiDaCampi({ ore: v.durata_ore, minuti: v.durata_min }),
      ore_mediazione: conOre && v.ore_mediazione !== '' ? Number(v.ore_mediazione) : null,
      modalita: v.modalita,
      comune_intervento: v.comune_intervento || null,
      attivita_istituzionale: v.attivita_istituzionale,
      progetto_id: v.attivita_istituzionale ? null : v.progetto_id || null,
      ha_stakeholder: v.ha_stakeholder === '' ? null : v.ha_stakeholder === 'si',
      stakeholder_id: v.ha_stakeholder === 'si' ? v.stakeholder_id || null : null,
      categoria_persona: v.categoria_persona || null,
      // Con la data di nascita la fascia non si invia: la calcola l'API.
      ...(conDataNascita ? {} : { fascia_eta: v.fascia_eta || null }),
      sottotipologia_codice: v.sottotipologia_codice || null,
      esito: v.esito || null,
      note: v.note || null,
      dati: v.dati,
    }
  }

  function mostraErrore(e: unknown) {
    setTentativo((n) => n + 1)
    if (e instanceof ErroreApi) {
      setCodiceErrore(e.codice ?? null)
      // «Salva» chiede tutti i campi di chiusura: il messaggio dice le due strade, con i nomi dei
      // comandi che l'operatore ha davanti (segnalazione del 19/10/2026).
      setErrore(e.codice === 'CHIUSURA_INCOMPLETA'
        ? (bozzaPossibile
          ? 'Per «Salva» mancano i dati elencati qui sotto: completali, oppure usa «Salva come bozza» e completa l’intervento più tardi.'
          : 'Per salvare mancano i dati elencati qui sotto: completali.')
        : e.message)
      setDettagli(e.dettagli ?? [])
      if (e.codice === 'CONSENSO_MANCANTE' && verifica) {
        setVerifica({ ...verifica, esito: 'bloccato', dettagli: e.dettagli ?? verifica.dettagli })
      }
    } else {
      setCodiceErrore(null)
      setErrore('Non è andata a buon fine: controlla la connessione e riprova.')
      setDettagli([])
    }
  }

  /**
   * Salva e poi esce: verso la scheda, oppure verso l'uscita in sospeso quando il salvataggio
   * arriva dalla domanda «Salva / Non salvare». Con un errore si resta sul modulo.
   */
  async function salva(stato: 'bozza' | 'completo' | null, versoInSospeso: boolean, confermaOrario = false) {
    setInCorso(true); setErrore(null); setCodiceErrore(null); setDettagli([]); setFuoriOrario(null)
    // Ore fuori misura (non intere, oltre 24): si dice qui, prima dell'API, che risponderebbe con i minuti.
    if (Number.isNaN(minutiDaCampi({ ore: valori.durata_ore, minuti: valori.durata_min }))) {
      setErrore('La durata non è valida.')
      setDettagli([{ campo: 'durata_minuti', messaggio: `Durata: ore intere da 0 a ${ORE_MASSIME}, minuti 0, 15, 30 o 45.` }])
      setTentativo((n) => n + 1)
      setInCorso(false)
      uscita.rimani()
      return
    }
    try {
      if (modifica) {
        // Solo i campi cambiati: una modifica non riscrive quello che non tocca, e la nota che
        // l'incarico non apre non si puo' ne' leggere ne' sovrascrivere.
        const attuali = campiApi(valori)
        const prima = campiApi(iniziali ?? valori)
        // Lo stato si invia solo se cambia. Con `stato` nel corpo l'API applica tutte le regole di
        // chiusura e l'obbligo delle ore: sull'intervento gia' completo la correzione dell'esito o
        // della nota si bloccherebbe se registrato con regole precedenti (senza ore prima di D-27,
        // su una tipologia poi storica). Senza `stato` le regole scattano solo sui campi di chiusura.
        const corpo: Record<string, unknown> = stato && stato !== statoRegistrato ? { stato } : {}
        for (const [chiave, valore] of Object.entries(attuali)) {
          if (chiave === 'note' && !registrato?.note_visibili) continue
          if (JSON.stringify(valore) !== JSON.stringify(prima[chiave])) corpo[chiave] = valore
        }
        await api.patch(`/interventi/${interventoId}`, corpo)
      } else {
        // Prima di chiamare l'API, le due scelte senza cui non si salva nemmeno la bozza.
        const mancanti: Dettaglio[] = [
          ...(!valori.percorso_id && !valori.tipologia
            ? [{ campo: 'tipologia_codice', messaggio: 'Tipologia di intervento: scegli l’area e la tipologia.' }] : []),
          ...(!valori.modalita ? [{ campo: 'modalita', messaggio: 'Modalità: serve anche per la bozza.' }] : []),
        ]
        if (mancanti.length) {
          setErrore(mancanti.length > 1 ? 'Mancano la tipologia e la modalità.'
            : mancanti[0].campo === 'modalita' ? 'Manca la modalità.' : 'Manca la tipologia di intervento.')
          setDettagli(mancanti)
          setTentativo((n) => n + 1)
          setInCorso(false)
          uscita.rimani()
          return
        }
        if (conRicontatto) {
          const erroriRicontatto = controllaRicontatto(ricontatto, 'ricontatto.')
          if (Object.keys(erroriRicontatto).length) {
            setErrore('Mancano alcuni dati del ricontatto.')
            setDettagli(Object.entries(erroriRicontatto).map(([campo, messaggio]) => ({ campo, messaggio })))
            setTentativo((n) => n + 1)
            setInCorso(false)
            uscita.rimani()
            return
          }
        }
        const campi = campiApi(valori)
        const sede = valori.sede_id || null
        const conIlRicontatto = conRicontatto
          ? { ricontatto: { ...corpoRicontatto(ricontatto, false), conferma_fuori_orario: confermaOrario } } : {}
        await api.post('/interventi', valori.percorso_id
          ? { ...campi, percorso_id: valori.percorso_id, sede_id: percorso?.sede_id ?? sede, stato, ...conIlRicontatto }
          : {
            ...campi, persona_id: id, sede_id: sede, stato, ...conIlRicontatto,
            nuovo_percorso: {
              tipologia_codice: valori.tipologia, sede_id: sede, progetto_id: campi.progetto_id,
            },
          })
      }
      setInCorso(false)
      uscita.esci(versoInSospeso ? undefined : `/persone/${id}`)
    } catch (e) {
      if (e instanceof ErroreApi && e.codice === 'FUORI_ORARIO') {
        setFuoriOrario({ testo: e.message, stato, verso: versoInSospeso })
      } else {
        mostraErrore(e)
      }
      setInCorso(false)
      uscita.rimani()
    }
  }

  async function scarta(versoInSospeso: boolean) {
    setInCorso(true); setErrore(null)
    try {
      await api.post('/interventi/scarto', corpoScarto)
    } catch (e) {
      // Fuori perimetro (404) non c'e' nulla da annotare ne' da salvare: si esce comunque. Con
      // un altro errore lo scarto non risulterebbe nel registro: si resta e si riprova.
      if (!(e instanceof ErroreApi && e.stato === 404)) {
        setErrore('Lo scarto non si è potuto registrare: riprovare.')
        setInCorso(false); setConfermaScarto(false); uscita.rimani()
        return
      }
    }
    setInCorso(false); setConfermaScarto(false)
    uscita.esci(versoInSospeso ? undefined : `/persone/${id}`)
  }

  if (errore && !scheda) return <Avviso tipo="errore">{errore}</Avviso>
  if (!scheda || !iniziali) return <Attesa testo="Apertura della scheda…" />

  if (registrato && !registrato.modificabile) {
    return (
      <>
        <Intestazione titolo="Intervento" sottotitolo={<Link to={`/persone/${id}`}>{scheda.etichetta}</Link>} />
        <Avviso tipo="informazione">
          L’intervento non si può modificare: è annullato o non rientra negli incarichi in corso.
        </Avviso>
      </>
    )
  }

  const erroreDi = (campo: string) =>
    dettagli.filter((d) => d.campo === campo).map((d) => d.messaggio).join(' ') || null
  const testoDettaglio = (d: Dettaglio) => {
    // I campi del ricontatto fissato insieme all'intervento hanno il loro nome, mai quello tecnico.
    if (d.campo.startsWith('ricontatto')) {
      const nome = ETICHETTE_RICONTATTO[d.campo.slice('ricontatto.'.length) as keyof typeof ETICHETTE_RICONTATTO]
      return nome ? `Ricontatto, ${nome.toLowerCase()}: ${d.messaggio}` : d.messaggio
    }
    const etichetta = etichettaCampo(schemaModulo, d.campo)
    return CAMPI_FISSI.has(d.campo) || d.messaggio.startsWith(etichetta) ? d.messaggio : `${etichetta}: ${d.messaggio}`
  }

  const sottotipologie = (tassonomia?.sottotipologie ?? [])
    .filter((s) => s.tipologia_codice === tipologiaScelta && s.attiva && !s.storica)
  const confermate = scheda.categorie.filter((c) => c.confermata)
  const titolo = !modifica ? 'Registra intervento'
    : statoRegistrato === 'completo' ? 'Modifica intervento' : 'Completa intervento'
  const statoAllUscita = !modifica || statoRegistrato === 'bozza' ? 'bozza'
    : statoRegistrato === 'completo' ? 'completo' : null
  const spiegazioneUscita = statoAllUscita === 'bozza'
    ? '«Salva» conserva l’intervento come bozza, da completare in seguito. «Non salvare» esce e scarta quanto inserito.'
    : '«Salva» registra le modifiche sull’intervento. «Non salvare» esce e lascia l’intervento com’era.'

  const erroreComune = erroreDi('comune_intervento')
  const erroreEnte = erroreDi('stakeholder_id')

  // Progetti in ordine alfabetico; per difetto quelli della sede del modulo (4/11/2026): la sede scelta per il
  // nuovo percorso, quella del percorso, o quella dell'intervento che si modifica. Gli sportelli valgono come la
  // loro sede. Il progetto gia' scelto resta nella tendina anche se non e' della sede.
  const sedeModulo = (modifica ? registrato?.sede_id : percorso?.sede_id || valori.sede_id) || null
  const sceltaProgetti = progettiDaProporre(progetti, {
    sedeId: sedeModulo, sedi, tutti: tuttiProgetti, scelto: valori.progetto_id || null,
  })
  const sedeDetta = sedeModulo ? sedi.find((s) => s.id === sedeModulo) : undefined
  const nomeDellaSede = sedeDetta ? `«${nomeSede(sedeDetta)}»` : 'scelta'
  const notaProgetto = [
    'Un progetto oppure l’attività istituzionale.',
    !sedeModulo ? null
      : tuttiProgetti ? 'Qui tutti i progetti, anche di altre sedi o senza sede.'
        : sceltaProgetti.dellaSede === 0 ? `Nessun progetto è ancora collegato alla sede ${nomeDellaSede}: qui tutti i progetti.`
          : `Qui i progetti della sede ${nomeDellaSede}.`,
    sceltaProgetti.sceltoFuoriSede ? `Il progetto scelto non è collegato alla sede ${nomeDellaSede}: resta scelto.` : null,
  ].filter(Boolean).join(' ')

  return (
    <div ref={radice}>
      <Intestazione
        titolo={titolo}
        sottotitolo={<>per <Link to={`/persone/${id}`}>{scheda.etichetta}</Link> · scheda n. {scheda.numero_scheda}</>}
      />

      {bloccato && verifica && (
        <Avviso tipo="errore">
          <strong>Consensi 1 e 2 non espressi: sulle schede nuove l’intervento non si registra.</strong>
          <ul className="elenco-avviso">
            {verifica.dettagli.map((d) => <li key={d.campo}>{d.messaggio}</li>)}
          </ul>
          <ComeRegistrareConsensi personaId={id} puoRegistrare={!!scheda.puo?.registrareConsensi} />
        </Avviso>
      )}
      {verifica?.esito === 'avviso' && (
        <Avviso tipo="attenzione">
          <strong>Scheda migrata senza i consensi 1 e 2 della Rev05.</strong> L’intervento si registra;
          i consensi vanno raccolti al primo contatto.
          <ul className="elenco-avviso">
            {verifica.dettagli.map((d) => <li key={d.campo}>{d.messaggio}</li>)}
          </ul>
        </Avviso>
      )}

      <Riquadro titolo="Percorso e tipologia">
        {!modifica && (
          <Scelta
            etichetta="Percorso"
            valore={valori.percorso_id}
            cambia={(v) => setValori((x) => ({
              ...x, percorso_id: v, area: '', tipologia: '', sottotipologia_codice: '', dati: {} }))}
            vuota="— nuovo percorso —"
            opzioni={percorsiUsabili.map((p) => ({
              valore: p.id,
              etichetta: `${p.tipologia} · aperto il ${new Date(p.data_apertura).toLocaleDateString('it-IT')}`,
            }))}
            aiuto="Il percorso è la presa in carico su una tipologia; l’intervento è la singola prestazione."
          />
        )}

        {/* Tipologia sempre dentro l'area (Note aggiuntive): prima l'area, poi le sole tipologie
            in uso di quell'area. */}
        {!modifica && !valori.percorso_id && (
          <div className="griglia-modulo">
            <Scelta
              etichetta="Area" obbligatorio
              valore={valori.area}
              cambia={(v) => setValori((x) => ({ ...x, area: v, tipologia: '', sottotipologia_codice: '', dati: {} }))}
              vuota="— seleziona —"
              opzioni={areeConTipologie.map((a) => ({ valore: a.codice, etichetta: `${a.numero}. ${a.nome}` }))}
            />
            <Scelta
              etichetta="Tipologia di intervento" obbligatorio
              disabilitato={!valori.area}
              valore={valori.tipologia}
              cambia={(v) => setValori((x) => ({ ...x, tipologia: v, sottotipologia_codice: '', dati: {} }))}
              vuota={valori.area ? '— seleziona —' : '— scegliere prima l’area —'}
              opzioni={tipologieAperte.filter((t) => t.area_codice === valori.area)
                .map((t) => ({ valore: t.codice, etichetta: t.nome }))}
              errore={erroreDi('tipologia_codice')}
            />
          </div>
        )}

        {(percorso || modifica) && (
          <div className="riga-etichette">
            <EtichettaArea codice={percorso?.area_codice ?? valori.area}
                           colore={aree.coloreDi(percorso?.area_codice ?? valori.area)} />
            <span className="medio">{percorso?.tipologia ?? registrato?.tipologia}</span>
          </div>
        )}
        {modifica && erroreDi('tipologia_codice') && (
          <Avviso tipo="attenzione">{erroreDi('tipologia_codice')}</Avviso>
        )}

        {sottotipologie.length > 0 && (
          <Scelta
            etichetta="Sottotipologia"
            valore={valori.sottotipologia_codice}
            cambia={(v) => cambia('sottotipologia_codice', v)}
            vuota="— seleziona —"
            opzioni={sottotipologie.map((s) => ({ valore: s.codice, etichetta: s.nome }))}
          />
        )}
      </Riquadro>

      <Riquadro titolo="L’intervento">
        <p className="campo-aiuto">
          I campi con * servono per «Salva», che chiude la scheda dell’intervento. Per «Salva come bozza» bastano
          data e modalità. La durata è facoltativa.
        </p>
        <div className="griglia-modulo">
          <Campo
            etichetta="Data" tipo="date" obbligatorio max={oggi()}
            valore={valori.data}
            cambia={(v) => cambia('data', v)}
            errore={erroreDi('data')}
          />
          {conOre && (
            <Campo
              etichetta="Ore di mediazione" tipo="number" obbligatorio min={0.5} max={10} passi={0.5}
              modalitaTastiera="decimal"
              valore={valori.ore_mediazione}
              cambia={(v) => setValori((x) => {
                // La durata segue le ore quando sono valide e resta correggibile a mano: le due
                // misure restano distinte (Q-D22), ma di norma coincidono. Ore a multipli di
                // mezz'ora da 0,5 a 10 (decisione del 16/09/2026): un'ora e mezza fa 1 ora e 30 minuti.
                const valide = Number.isInteger(Number(v) * 2) && Number(v) >= 0.5 && Number(v) <= 10
                const durata = valide ? campiDaMinuti(Number(v) * 60) : null
                return { ...x, ore_mediazione: v, ...(durata ? { durata_ore: durata.ore, durata_min: durata.minuti } : {}) }
              })}
              aiuto="Mezz’ora o multipli, da 0,5 a 10. La durata si aggiorna di conseguenza."
              errore={erroreDi('ore_mediazione')}
            />
          )}
          <CampoDurata ore={valori.durata_ore} minuti={valori.durata_min} errore={erroreDi('durata_minuti')}
                       cambia={(ore, minuti) => setValori((x) => ({ ...x, durata_ore: ore, durata_min: minuti }))} />
          <Scelta
            etichetta="Modalità" obbligatorio
            valore={valori.modalita}
            cambia={(v) => cambia('modalita', v)}
            vuota="— seleziona —"
            opzioni={modalita.voci.map((m) => ({ valore: m.codice, etichetta: m.nome }))}
            errore={erroreDi('modalita')}
          />
          {!modifica && (
            <Scelta
              etichetta="Sede"
              valore={percorso?.sede_id ?? valori.sede_id}
              cambia={(v) => cambia('sede_id', v)}
              disabilitato={!!percorso?.sede_id}
              vuota="— seleziona —"
              opzioni={opzioniSedi(sedi)}
              aiuto={percorso?.sede_id
                ? 'È la sede del percorso.'
                : percorso ? 'Il percorso non ha una sede: vale quella scelta qui.'
                  : 'Il nuovo percorso si apre in questa sede. All’inizio è quella della scheda.'}
              errore={erroreDi('sede_id')}
            />
          )}

          {/* Il comune si sceglie fra i suggerimenti di un <datalist>, che i componenti di base
              non prevedono: il campo resta scritto a mano e ripete il markup di `Campo`. */}
          <div className={`campo${erroreComune ? ' campo-errato' : ''}`}>
            <label htmlFor="comune">
              Comune dell’intervento<span className="campo-obbligatorio" aria-hidden="true"> *</span>
            </label>
            <input id="comune" list="elenco-comuni" value={valori.comune_testo}
                   placeholder="Nome del comune" aria-describedby="comune-nota" aria-required="true"
                   aria-invalid={erroreComune ? true : undefined}
                   onChange={(e) => {
                     const testo = e.target.value
                     const scelto = comuni.find((c) => `${c.nome} (${c.provincia_sigla})` === testo)
                     setValori((v) => ({ ...v, comune_testo: testo, comune_intervento: scelto?.codice_istat ?? '' }))
                   }} />
            <datalist id="elenco-comuni">
              {comuni.map((c) => <option key={c.codice_istat} value={`${c.nome} (${c.provincia_sigla})`} />)}
            </datalist>
            {erroreComune
              ? <p className="campo-errore" id="comune-nota" role="alert">{erroreComune}</p>
              : <p className="campo-aiuto" id="comune-nota">Luogo in cui si svolge l’intervento, scelto fra i suggerimenti.</p>}
          </div>

          <Scelta
            etichetta="Categoria di utenti" obbligatorio
            disabilitato={!!registrato?.categoria_nascosta}
            valore={valori.categoria_persona}
            cambia={(v) => cambia('categoria_persona', v)}
            vuota={registrato?.categoria_nascosta ? '— categoria riservata —' : '— seleziona —'}
            opzioni={categorie.voci.map((c) => ({ valore: c.codice, etichetta: c.nome }))}
            aiuto={registrato?.categoria_nascosta
              ? 'Categoria riservata: non si vede né si modifica con gli incarichi in corso.'
              : confermate.length ? `Confermate sulla scheda: ${confermate.map((c) => c.nome).join(', ')}.` : undefined}
            errore={erroreDi('categoria_persona')}
          />

          {conDataNascita
            ? (
              <Scelta
                etichetta="Fascia d’età" obbligatorio disabilitato
                valore={fasciaCalcolata ?? ''}
                cambia={() => {}}
                vuota="— non calcolabile —"
                opzioni={fasce.voci.map((f) => ({ valore: f.codice, etichetta: f.nome }))}
                aiuto="Calcolata dalla data di nascita alla data dell’intervento."
                errore={erroreDi('fascia_eta')}
              />
            )
            : (
              <Scelta
                etichetta="Fascia d’età" obbligatorio
                valore={valori.fascia_eta}
                cambia={(v) => cambia('fascia_eta', v)}
                vuota="— seleziona —"
                opzioni={fasce.voci.map((f) => ({ valore: f.codice, etichetta: f.nome }))}
                aiuto="La persona non ha data di nascita: la fascia si sceglie a mano."
                errore={erroreDi('fascia_eta')}
              />
            )}
        </div>

        <div className="griglia-modulo">
          <Scelta
            etichetta="Ente coinvolto" obbligatorio
            valore={valori.ha_stakeholder}
            cambia={(v) => setValori((x) => (v === 'si'
              ? { ...x, ha_stakeholder: 'si' }
              : { ...x, ha_stakeholder: v as '' | 'no', stakeholder_id: '', stakeholder_testo: '' }))}
            vuota="— seleziona —"
            opzioni={[{ valore: 'si', etichetta: 'Sì' }, { valore: 'no', etichetta: 'No' }]}
            errore={erroreDi('ha_stakeholder')}
          />
          {valori.ha_stakeholder === 'si' && (
            <div className={`campo${erroreEnte ? ' campo-errato' : ''}`}>
              <label htmlFor="ente">
                Ente<span className="campo-obbligatorio" aria-hidden="true"> *</span>
              </label>
              <input id="ente" list="elenco-enti" value={valori.stakeholder_testo}
                     placeholder="Nome dell’ente" aria-describedby="ente-nota" aria-required="true"
                     aria-invalid={erroreEnte ? true : undefined}
                     onChange={(e) => {
                       const testo = e.target.value
                       const scelto = enti.find((x) => `${x.nome}${x.sigla ? ` (${x.sigla})` : ''}` === testo)
                       setValori((v) => ({ ...v, stakeholder_testo: testo, stakeholder_id: scelto?.id ?? '' }))
                     }} />
              <datalist id="elenco-enti">
                {enti.map((x) => <option key={x.id} value={`${x.nome}${x.sigla ? ` (${x.sigla})` : ''}`} />)}
              </datalist>
              {erroreEnte
                ? <p className="campo-errore" id="ente-nota" role="alert">{erroreEnte}</p>
                : (
                  <p className="campo-aiuto" id="ente-nota">
                    {entiConsultabili
                      ? 'Scelto fra i suggerimenti dell’anagrafica enti.'
                      : 'L’anagrafica enti non rientra negli incarichi in corso: l’ente non si può scegliere.'}
                  </p>
                )}
            </div>
          )}
        </div>

        <Spunta
          etichetta="Attività istituzionale (nessun progetto)"
          valore={valori.attivita_istituzionale}
          cambia={(v) => setValori((x) => ({ ...x, attivita_istituzionale: v, progetto_id: v ? '' : x.progetto_id }))}
        />
        {!valori.attivita_istituzionale && (
          <div className="interventi-progetto">
            <Scelta
              etichetta="Progetto" obbligatorio
              valore={valori.progetto_id}
              cambia={(v) => cambia('progetto_id', v)}
              vuota="— seleziona —"
              opzioni={sceltaProgetti.voci.map((p) => ({ valore: p.id, etichetta: etichettaProgetto(p) }))}
              aiuto={notaProgetto}
              errore={erroreDi('progetto_id')}
            />
            {sedeModulo && sceltaProgetti.dellaSede > 0 && sceltaProgetti.fuori > 0 && (
              <Bottone variante="discreto" dimensione="piccolo" aria-pressed={tuttiProgetti}
                       onClick={() => setTuttiProgetti((v) => !v)}>
                {tuttiProgetti ? 'Solo i progetti della sede' : 'Mostra tutti i progetti'}
              </Bottone>
            )}
          </div>
        )}
      </Riquadro>

      {schemaModulo && schemaModulo.campi.length > 0 && (
        <Riquadro titolo="Campi della tipologia">
          <div className="griglia-modulo">
            {schemaModulo.campi.map((c) => (
              <CampoModulo key={c.codice} campo={c} valore={valori.dati[c.codice]} errore={erroreDi(c.codice)}
                           cambia={(v) => setValori((x) => ({ ...x, dati: { ...x.dati, [c.codice]: v } }))} />
            ))}
          </div>
        </Riquadro>
      )}

      <Riquadro titolo="Esito e note">
        <Campo
          etichetta="Esito" maxLength={500}
          valore={valori.esito}
          cambia={(v) => cambia('esito', v)}
        />
        <AreaTesto
          etichetta="Note" righe={4} maxLength={5000}
          valore={valori.note}
          cambia={(v) => cambia('note', v)}
          disabilitato={modifica && !registrato?.note_visibili}
          aiuto={modifica && !registrato?.note_visibili
            ? 'La nota non rientra negli incarichi in corso: resta invariata.'
            : 'Le note sono cifrate con la chiave del beneficiario.'}
        />
      </Riquadro>

      {!modifica && ricontatti.dati && (
        <Riquadro titolo="Ricontatto">
          <Spunta etichetta="Fissa anche un ricontatto" valore={conRicontatto} cambia={setConRicontatto}
                  aiuto="Per esempio un appuntamento preso durante l’intervento. Resta sullo stesso percorso e si
                         ritrova nella scheda, nella pagina Ricontatti e nel cruscotto di chi lo segue." />
          {conRicontatto && (
            <CampiRicontatto valori={ricontatto} cambia={setRicontatto} colleghi={ricontatti.dati.colleghi}
                             prefisso="ricontatto."
                             errori={Object.fromEntries(dettagli.filter((d) => d.campo.startsWith('ricontatto.'))
                               .map((d) => [d.campo, d.messaggio]))} />
          )}
        </Riquadro>
      )}

      {fuoriOrario && (
        <AvvisoFuoriOrario testo={`Ricontatto: ${fuoriOrario.testo}`} inCorso={inCorso}
                           conferma={() => void salva(fuoriOrario.stato, fuoriOrario.verso, true)}
                           rinuncia={() => setFuoriOrario(null)} />
      )}
      {/* Esito del salvataggio accanto ai comandi: in testa alla pagina resterebbe fuori dalla vista. */}
      {errore && (
        <Avviso tipo="errore">
          <strong>{errore}</strong>
          {dettagli.length > 0 && (
            <ul className="elenco-avviso">
              {dettagli.map((d, i) => <li key={i}>{testoDettaglio(d)}</li>)}
            </ul>
          )}
          {codiceErrore === 'CONSENSO_MANCANTE' && (
            <ComeRegistrareConsensi personaId={id} puoRegistrare={!!scheda.puo?.registrareConsensi} />
          )}
        </Avviso>
      )}

      <div className="riga">
        <Bottone variante="primario" inCorso={inCorso} disabled={bloccato}
                 onClick={() => void salva('completo', false)}>
          Salva
        </Bottone>
        {bozzaPossibile && (
          <Bottone variante="secondario" disabled={inCorso || bloccato} onClick={() => void salva('bozza', false)}>
            Salva come bozza
          </Bottone>
        )}
        <span className="spinta">
          <Bottone variante="critico" disabled={inCorso}
                   onClick={() => (modificato ? setConfermaScarto(true) : void scarta(false))}>
            Errore – non salvare scheda
          </Bottone>
        </span>
      </div>

      {confermaScarto && (
        <Modale
          titolo="Errore – non salvare scheda"
          chiudi={() => setConfermaScarto(false)}
          azioni={
            <>
              <Bottone variante="secondario" disabled={inCorso} onClick={() => setConfermaScarto(false)}>
                Torna al modulo
              </Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void scarta(false)}>
                Non salvare
              </Bottone>
            </>
          }
        >
          <p>
            {modifica
              ? 'Le modifiche non si salvano e l’intervento resta com’era.'
              : 'L’intervento non si registra e quanto inserito si perde.'}
            {' '}Nel registro eventi resta solo l’annotazione dello scarto, senza i valori inseriti.
          </p>
        </Modale>
      )}

      {uscita.domanda && (
        <DomandaUscita
          spiegazione={spiegazioneUscita}
          inCorso={inCorso}
          salva={() => void salva(statoAllUscita, true)}
          nonSalvare={() => void scarta(true)}
          rimani={uscita.rimani}
        />
      )}
    </div>
  )
}

/**
 * Dove si registrano i consensi che mancano: la sezione Consensi della scheda, con un collegamento
 * che ci porta. Chi non li registra (per esempio la Direzione) sa a chi chiederli.
 */
function ComeRegistrareConsensi({ personaId, puoRegistrare }: { personaId: string; puoRegistrare: boolean }) {
  return puoRegistrare
    ? (
      <p>
        Registrali nella <Link to={`/persone/${personaId}#consensi`}>sezione Consensi della scheda</Link>, poi torna
        qui a registrare l’intervento.
      </p>
    )
    : <p>Con i tuoi incarichi non puoi registrarli: chiedi a un collega che segue la persona di raccoglierli.</p>
}

/**
 * Durata in ore e minuti (4/11/2026, Osservazioni 6 di Carmem): due campi facoltativi, le ore intere e i minuti
 * a quarti d'ora. Un intervento registrato prima con altri minuti (per esempio 20) li mostra fra le scelte, cosi'
 * la modifica non li cambia senza che l'operatore lo voglia.
 */
function CampoDurata({ ore, minuti, errore, cambia }: {
  ore: string; minuti: string; errore: string | null; cambia: (ore: string, minuti: string) => void
}) {
  const id = useId()
  const scelte = MINUTI_AMMESSI.map(String)
  if (minuti && !scelte.includes(minuti)) scelte.push(minuti)
  return (
    <fieldset className={`campo interventi-durata${errore ? ' campo-errato' : ''}`}
              aria-describedby={`${id}-nota`}>
      <legend>Durata</legend>
      <div className="interventi-durata-parti">
        <span className="interventi-durata-parte">
          <label htmlFor={`${id}-ore`}>Ore</label>
          <input id={`${id}-ore`} type="number" min={0} max={ORE_MASSIME} step={1} inputMode="numeric"
                 value={ore} onChange={(e) => cambia(e.target.value, minuti)}
                 aria-invalid={errore ? true : undefined} />
        </span>
        <span className="interventi-durata-parte">
          <label htmlFor={`${id}-minuti`}>Minuti</label>
          <select id={`${id}-minuti`} value={minuti} onChange={(e) => cambia(ore, e.target.value)}
                  aria-invalid={errore ? true : undefined}>
            <option value="">—</option>
            {scelte.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </span>
      </div>
      {errore
        ? <p className="campo-errore" id={`${id}-nota`} role="alert">{errore}</p>
        : <p className="campo-aiuto" id={`${id}-nota`}>Facoltativa.</p>}
    </fieldset>
  )
}
